import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { startEditImagesBatch } from "../../../../actions/user";
import { getIsBlockedActions } from "../../../../actions/getters";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import { validateModalImageEditTextFields } from "../../../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import {
  PROMPTS_CHECKPOINT_SIZE,
  mergeAttachmentUrls,
  mergeAttachmentFileNames,
} from "./imageEditCheckpoint";

export const BATCH_ITEM_STATUS = {
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
};

const buildInitialItemsMap = (initialItems = []) => {
  const map = {};

  initialItems.forEach((item) => {
    if (!item?.selectionId || !item?.url) return;

    map[item.selectionId] = {
      selectionId: item.selectionId,
      sourceItem: item.sourceItem,
      index: item.index,
      blockBaseUrl: item.url,
      versions: {
        1: { url: item.url, status: BATCH_ITEM_STATUS.SUCCESS },
      },
    };
  });

  return map;
};

const getBlockBaseUrl = (itemState) =>
  itemState?.blockBaseUrl || itemState?.versions?.[1]?.url || null;

export const useBatchImageEdit = ({ initialItems = [], isOpen }) => {
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );

  const [itemsById, setItemsById] = useState(() =>
    buildInitialItemsMap(initialItems)
  );
  const [visibleVersion, setVisibleVersion] = useState(1);
  const [globalVersion, setGlobalVersion] = useState(1);
  const [prompt, setPrompt] = useState("");
  const [checkpointPromptsHistory, setCheckpointPromptsHistory] = useState([]);
  const [isGenerateInProgress, setIsGenerateInProgress] = useState(false);
  const [retryingSelectionIds, setRetryingSelectionIds] = useState([]);

  const accumulatedAttachmentUrlsRef = useRef([]);
  const accumulatedAttachmentFileNamesRef = useRef({});
  const lastPromptsOrderedRef = useRef([]);

  const resetAttachmentAccumulation = useCallback(() => {
    accumulatedAttachmentUrlsRef.current = [];
    accumulatedAttachmentFileNamesRef.current = {};
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setItemsById(buildInitialItemsMap(initialItems));
    setVisibleVersion(1);
    setGlobalVersion(1);
    setPrompt("");
    setCheckpointPromptsHistory([]);
    lastPromptsOrderedRef.current = [];
    resetAttachmentAccumulation();
    setIsGenerateInProgress(false);
    setRetryingSelectionIds([]);
  }, [isOpen, initialItems, resetAttachmentAccumulation]);

  const isRetryInProgress = retryingSelectionIds.length > 0;
  const isJobInProgress = isGenerateInProgress || isRetryInProgress;

  const versionOptions = useMemo(
    () => Array.from({ length: globalVersion }, (_, index) => index + 1),
    [globalVersion]
  );

  const items = useMemo(() => Object.values(itemsById), [itemsById]);

  const markItemsPending = useCallback((targetVersion, selectionIds) => {
    setItemsById((prev) => {
      const next = { ...prev };

      selectionIds.forEach((selectionId) => {
        if (!next[selectionId]) return;

        next[selectionId] = {
          ...next[selectionId],
          versions: {
            ...next[selectionId].versions,
            [targetVersion]: { status: BATCH_ITEM_STATUS.PENDING },
          },
        };
      });

      return next;
    });
  }, []);

  const applyBatchResults = useCallback((targetVersion, results = []) => {
    setItemsById((prev) => {
      const next = { ...prev };

      results.forEach((result) => {
        const selectionId = result?.selectionId;
        if (!selectionId || !next[selectionId]) return;

        const versions = { ...next[selectionId].versions };

        if (result.ok && result.url) {
          versions[targetVersion] = {
            url: result.url,
            status: BATCH_ITEM_STATUS.SUCCESS,
          };
        } else {
          versions[targetVersion] = {
            status: BATCH_ITEM_STATUS.ERROR,
            error: result.error || result.message || "",
          };
        }

        next[selectionId] = {
          ...next[selectionId],
          versions,
        };
      });

      return next;
    });
  }, []);

  const applyCheckpointBlockBases = useCallback((results = []) => {
    setItemsById((prev) => {
      const next = { ...prev };

      results.forEach((result) => {
        const selectionId = result?.selectionId;
        if (!selectionId || !result?.ok || !result?.url || !next[selectionId]) {
          return;
        }

        next[selectionId] = {
          ...next[selectionId],
          blockBaseUrl: result.url,
        };
      });

      return next;
    });
  }, []);

  const markItemsErrored = useCallback((targetVersion, selectionIds, error) => {
    setItemsById((prev) => {
      const next = { ...prev };

      selectionIds.forEach((selectionId) => {
        if (!next[selectionId]) return;

        next[selectionId] = {
          ...next[selectionId],
          versions: {
            ...next[selectionId].versions,
            [targetVersion]: {
              status: BATCH_ITEM_STATUS.ERROR,
              error: error || "",
            },
          },
        };
      });

      return next;
    });
  }, []);

  const runBatchEdit = useCallback(
    async ({
      targetVersion,
      batchItems,
      prompts,
      attachmentUrls = [],
      trackAsGenerate = true,
    }) => {
      const selectionIds = batchItems.map((item) => item.selectionId);
      const promptsOrdered = Array.isArray(prompts) ? prompts : [];
      const resolvedAttachmentUrls = Array.isArray(attachmentUrls)
        ? attachmentUrls
        : [];

      markItemsPending(targetVersion, selectionIds);
      if (trackAsGenerate) {
        setIsGenerateInProgress(true);
      }

      try {
        const response = await dispatch(
          startEditImagesBatch({
            items: batchItems,
            prompts: promptsOrdered,
            attachmentUrls: resolvedAttachmentUrls,
          })
        );

        const batchResults = Array.isArray(response?.data?.results)
          ? response.data.results
          : Array.isArray(response?.results)
            ? response.results
            : null;

        if (response?.ok && batchResults) {
          applyBatchResults(targetVersion, batchResults);
          return { ok: true, results: batchResults };
        }

        const errorMessage =
          typeof response?.message === "string" ? response.message : "";
        markItemsErrored(targetVersion, selectionIds, errorMessage);
        return { ok: false, results: batchResults || [] };
      } catch (error) {
        const errorMessage =
          typeof error?.message === "string" ? error.message : "";
        markItemsErrored(targetVersion, selectionIds, errorMessage);
        return { ok: false, results: [] };
      } finally {
        if (trackAsGenerate) {
          setIsGenerateInProgress(false);
        }
      }
    },
    [applyBatchResults, dispatch, markItemsErrored, markItemsPending]
  );

  const validatePrompt = useCallback(
    (editPrompt) => {
      try {
        validateModalImageEditTextFields({
          prompt: editPrompt,
          isPlanActive,
          isTrial,
        });
        return true;
      } catch (error) {
        console.error("Batch image edit validation failed:", error);
        return false;
      }
    },
    [isPlanActive, isTrial]
  );

  const applyCheckpointAfterSuccess = useCallback(
    (promptsOrdered, batchResults) => {
      const completedBlockSize = promptsOrdered.length;

      if (completedBlockSize >= PROMPTS_CHECKPOINT_SIZE) {
        applyCheckpointBlockBases(batchResults);
        setCheckpointPromptsHistory([]);
        resetAttachmentAccumulation();
        return;
      }

      setCheckpointPromptsHistory(promptsOrdered);
    },
    [applyCheckpointBlockBases, resetAttachmentAccumulation]
  );

  const generate = useCallback(
    async (promptOverride, attachmentUrls = [], attachmentFileNames = {}) => {
      const trimmedPrompt = (promptOverride ?? prompt).trim();
      if (!trimmedPrompt || isJobInProgress || items.length === 0) {
        return { succeeded: false };
      }

      if (!validatePrompt(trimmedPrompt)) return { succeeded: false };

      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          showUsageConfirmationAlert: true,
          multiplier: items.length,
        })
      );
      if (isBlocked) return { cancelled: true };

      const targetVersion = globalVersion + 1;
      const promptsOrdered = [...checkpointPromptsHistory, trimmedPrompt];
      const resolvedAttachmentUrls = Array.isArray(attachmentUrls)
        ? attachmentUrls
        : [];
      const mergedAttachmentUrls = mergeAttachmentUrls(
        accumulatedAttachmentUrlsRef.current,
        resolvedAttachmentUrls
      );
      const mergedAttachmentFileNames = mergeAttachmentFileNames(
        accumulatedAttachmentFileNamesRef.current,
        attachmentFileNames
      );

      const batchItems = items
        .map((itemState) => {
          const sourceUrl = getBlockBaseUrl(itemState);
          if (!sourceUrl) return null;

          return {
            selectionId: itemState.selectionId,
            url: sourceUrl,
          };
        })
        .filter(Boolean);

      if (batchItems.length === 0) return { succeeded: false };

      lastPromptsOrderedRef.current = promptsOrdered;
      setPrompt(trimmedPrompt);
      setVisibleVersion(targetVersion);

      const { ok, results } = await runBatchEdit({
        targetVersion,
        batchItems,
        prompts: promptsOrdered,
        attachmentUrls: mergedAttachmentUrls,
        trackAsGenerate: true,
      });

      if (ok) {
        accumulatedAttachmentUrlsRef.current = mergedAttachmentUrls;
        accumulatedAttachmentFileNamesRef.current = mergedAttachmentFileNames;
        applyCheckpointAfterSuccess(promptsOrdered, results);
        setGlobalVersion(targetVersion);
        return { succeeded: true, targetVersion };
      }

      return { succeeded: false, targetVersion };
    },
    [
      ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      applyCheckpointAfterSuccess,
      checkpointPromptsHistory,
      dispatch,
      globalVersion,
      isJobInProgress,
      items,
      prompt,
      runBatchEdit,
      validatePrompt,
    ]
  );

  const retryItem = useCallback(
    async (selectionId) => {
      const promptsOrdered = lastPromptsOrderedRef.current;
      const itemState = itemsById[selectionId];
      const targetVersion = visibleVersion;

      if (
        !selectionId ||
        !itemState ||
        targetVersion <= 1 ||
        isGenerateInProgress ||
        retryingSelectionIds.includes(selectionId) ||
        promptsOrdered.length === 0
      ) {
        return;
      }

      const versionEntry = itemState.versions[targetVersion];
      if (versionEntry?.status !== BATCH_ITEM_STATUS.ERROR) return;

      const sourceUrl = getBlockBaseUrl(itemState);
      if (!sourceUrl) return;

      const latestPrompt = promptsOrdered[promptsOrdered.length - 1];
      if (!validatePrompt(latestPrompt)) return;

      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          showUsageConfirmationAlert: true,
          multiplier: 1,
        })
      );
      if (isBlocked) return;

      setRetryingSelectionIds((prev) =>
        prev.includes(selectionId) ? prev : [...prev, selectionId]
      );

      try {
        await runBatchEdit({
          targetVersion,
          batchItems: [{ selectionId, url: sourceUrl }],
          prompts: promptsOrdered,
          attachmentUrls: accumulatedAttachmentUrlsRef.current,
          trackAsGenerate: false,
        });
      } finally {
        setRetryingSelectionIds((prev) =>
          prev.filter((id) => id !== selectionId)
        );
      }
    },
    [
      ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      dispatch,
      isGenerateInProgress,
      itemsById,
      retryingSelectionIds,
      runBatchEdit,
      validatePrompt,
      visibleVersion,
    ]
  );

  const getVisibleEntry = useCallback(
    (itemState) => {
      if (!itemState) return null;

      const directEntry = itemState.versions[visibleVersion];
      if (directEntry) return directEntry;

      for (let version = visibleVersion; version >= 1; version -= 1) {
        if (itemState.versions[version]) {
          return itemState.versions[version];
        }
      }

      return itemState.versions[1] || null;
    },
    [visibleVersion]
  );

  const hasApplyableSuccesses = useMemo(() => {
    if (visibleVersion <= 1) return false;

    return items.some(
      (itemState) =>
        itemState.versions[visibleVersion]?.status === BATCH_ITEM_STATUS.SUCCESS
    );
  }, [items, visibleVersion]);

  const getSuccessfulItemsForApply = useCallback(() => {
    return items
      .filter(
        (itemState) =>
          itemState.versions[visibleVersion]?.status ===
          BATCH_ITEM_STATUS.SUCCESS
      )
      .map((itemState) => ({
        selectionId: itemState.selectionId,
        url: itemState.versions[visibleVersion].url,
        sourceItem: itemState.sourceItem,
        index: itemState.index,
      }));
  }, [items, visibleVersion]);

  return {
    items,
    visibleVersion,
    setVisibleVersion,
    globalVersion,
    versionOptions,
    prompt,
    setPrompt,
    isJobInProgress,
    isGenerateInProgress,
    isRetryInProgress,
    retryingSelectionIds,
    generate,
    retryItem,
    hasApplyableSuccesses,
    getSuccessfulItemsForApply,
    getVisibleEntry,
  };
};
